import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  highlight?: number;
  label?: string;
}
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5940">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-5941">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5942">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-5943">
            <span className="text-2xl" data-id="element-5944">🔤</span>
            <div className="text-left" data-id="element-5945">
              <div className="text-sm font-bold text-green-900" data-id="element-5946">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-5947">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-5948">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-5949">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-5950">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-5951">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-5952">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-5953">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-5954">

                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-5955">

                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-5956">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                            `} data-id="element-5957">

                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-5958">
                      <span className="text-xs font-bold" data-id="element-5959">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-5960">

                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-5961">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5962">
            <div className="flex items-start gap-3" data-id="element-5963">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5964">
                <span className="text-blue-600 text-lg" data-id="element-5965">👀</span>
              </div>
              <div data-id="element-5966">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-5967">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5968">
                  <p data-id="element-5969">
                    Het woord <strong data-id="element-5970">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-5971">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-5972">
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-5973">

                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-5974">
                    <p className="flex items-center gap-2" data-id="element-5975">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-5976"></span>
                      <span data-id="element-5977">
                        <strong data-id="element-5978">Groen (○):</strong> Open lettergreep = lange
                        klank (één medeklinker na de klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-5979">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-5980"></span>
                      <span data-id="element-5981">
                        <strong data-id="element-5982">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (twee medeklinkers na de klinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-5983">
            <div className="flex items-start gap-3" data-id="element-5984">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-5985">
                <span className="text-emerald-600 text-lg" data-id="element-5986">📖</span>
              </div>
              <div data-id="element-5987">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5988">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-5989">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-5990">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-5991">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-5992">
                      Eindigt op een <strong data-id="element-5993">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-5994">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-5995">
                      Voorbeeld: <strong data-id="element-5996">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-5997">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-5998">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-5999">
                      Eindigt op een <strong data-id="element-6000">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-6001">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-6002">
                      Voorbeeld: <strong data-id="element-6003">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-6004">
            <div className="flex items-start gap-3" data-id="element-6005">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-6006">
                <span className="text-indigo-600 text-lg" data-id="element-6007">✍️</span>
              </div>
              <div data-id="element-6008">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-6009">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6010">
                  <p data-id="element-6011">
                    <strong data-id="element-6012">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-6013">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-6014">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-6015">
                    <strong data-id="element-6016">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-6017">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-6018">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-6019">
            <div className="flex items-start gap-3" data-id="element-6020">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-6021">
                <span className="text-amber-600 text-lg" data-id="element-6022">💡</span>
              </div>
              <div data-id="element-6023">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-6024">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6025">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-6026">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-6027">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-6028">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-6029">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-6030">open</strong> en schrijf je{' '}
                  <strong data-id="element-6031">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}